import type { RNVJNavigationTrigger } from "./types/RNVJNavigationTrigger";
import type { NativeContextRNProps } from "../../shared/props/NativeContextRNProps";
import type { RestResponse } from "../native-module/types/ViewJobNativeModule";
import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";

import { AppVJLoadedContent } from "./AppVJLoadedContent";
import { NavigationBar } from "./components/core/JobComponent/NavigationBar/NavigationBar";
import { ConditionalSafeAreaWrapper } from "./components/core/SafeAreaWrapper";
import ViewJobDataContext, {
  type IViewJobDataContext,
  type JobClickTrackingSelectedJobAttributes,
} from "./context/ViewJobDataContext";
import { useScreenshotShare } from "./hooks/useScreenshotShare";
import { makeVJJsonUrl } from "./lib/makeVJJsonUrl";
import useNativeQuery from "./lib/useNativeQuery";
import { useViewJobAndroidBackHandler } from "./lib/useViewJobAndroidBackHandler";
import { getPlatformOptions } from "./PlatformOptions";
import { getDomain } from "./util/getDomain";
import { useGetApolloDefaultOptions } from "./util/useGetApolloDefaultOptions";
import { PROVIDER_NAME } from "./util/VJConstants";
import { fromParamUtils } from "./utils/fromParamUtils";
import { ViewJobLoadingState } from "./ViewJobLoadingState";
import { createViewjobOneGraphModel } from "./viewjobOneGraphModel";
import { OneGraphApolloClientProvider } from "../../shared/apollo";
import apolloErrorLink from "../../shared/apollo/apolloErrorLink";
import { useLocale } from "../../shared/I18n";
import { useLogFirstUserInteractionWithHandlers } from "../../shared/logging/interaction-tracker";
import { LoggerContext, LOGGER } from "../../shared/logging/logger";
import fetchNativeOneGraphOperation from "../../shared/native-modules/core/fetchNativeOneGraphQuery";
import { usePerformanceMeasurement } from "../../shared/util/performance";
import { useIsFullScreen } from "../../shared/util/ScreenType";
import { logVJUnmount } from "../native-module/logVJUmount";
import { onDeepLinkLoadCompleted } from "../native-module/onDeepLinkLoadCompleted";

import React, { useEffect, useMemo, useRef } from "react";
import { Platform, StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export interface AppContentProps {
  readonly url: string | null;
  readonly loggingUrl: string | null;
  readonly prforceGroups: string | null;
  readonly nativeContext?: NativeContextRNProps;
  readonly trigger?: RNVJNavigationTrigger;
  readonly source?: string;
  readonly reload?: () => void;
  readonly renderStarts?: number;
  readonly preloadedVJData?: model.viewjob.ViewJobClientSideModel & {
    jobClickTrackingAttributes?: JobClickTrackingSelectedJobAttributes;
    pageLoadSignalAlreadyEmitted?: boolean;
  };
  readonly viewJobSource: "ViewJob" | "ViewJobFeed";
  getVJData: (url: string) => Promise<RestResponse<model.viewjob.ViewJobClientSideModel>>;
  /** Unused by the REST path; present so the shared App switch accepts the prop. */
  readonly apolloClient?: ApolloClient<NormalizedCacheObject>;
}

export function AppContent({
  url,
  getVJData,
  prforceGroups,
  nativeContext,
  trigger,
  source,
  renderStarts,
  preloadedVJData,
  viewJobSource,
}: AppContentProps) {
  const locale = useLocale();
  useViewJobAndroidBackHandler();
  const errorLink = useMemo(
    () => apolloErrorLink({ provider: "ViewJob", nativeContext }),
    [nativeContext],
  );

  const defaultOptions = useGetApolloDefaultOptions({
    ctk: nativeContext?.ctk,
    locale,
  });

  const platformOptions = useMemo(() => getPlatformOptions(), []);

  // Performance measurement for TTFF, TTI, and FID
  const performanceResult = usePerformanceMeasurement({
    provider: "viewjob",
    componentName: "AppContent",
    grps: nativeContext?.proctorGroups,
  });
  const { markInteractive, panResponder: originalPanResponder } = performanceResult;

  const [getViewJobData, queryState, retryRequest, fetchStartTimeMs, fetchEndTimeMs] =
    useNativeQuery<RestResponse<model.viewjob.ViewJobClientSideModel>>(
      PROVIDER_NAME,
      url != null ? makeVJJsonUrl(url, locale, prforceGroups) : null,
      getVJData,
      {
        nativeContext,
        trigger,
        source,
      },
    );

  const queryStatus = preloadedVJData ? "success" : queryState.status;

  useEffect(() => {
    if (!preloadedVJData) {
      void getViewJobData();
    }
  }, [getViewJobData, preloadedVJData]);

  const withTopSafeArea = useIsFullScreen();

  useEffect(() => {
    // for android, we need to notify the native module that the deep link has loaded
    // but only  if the query is done
    if (["success", "error"].includes(queryStatus)) {
      void onDeepLinkLoadCompleted();
    }
  }, [queryStatus]);

  const viewJobModel =
    preloadedVJData ?? (queryState.status === "success" ? queryState.data.body : null);
  const viewjobOneGraphModel = useMemo(
    () => (viewJobModel ? createViewjobOneGraphModel(viewJobModel, locale) : undefined),
    [locale, viewJobModel],
  );
  const viewJobContextValue = useMemo<IViewJobDataContext>(
    () => ({
      initialData: viewJobModel || undefined,
      domain: getDomain(url),
      viewjobSourceUrl: url ?? "",
      viewjobOneGraphModel,
      proctorGroups: nativeContext?.proctorGroups,
      from: fromParamUtils(url),
      applyLinkLoading: false,
      jobClickTrackingAttributes: preloadedVJData?.jobClickTrackingAttributes,
    }),
    [
      nativeContext?.proctorGroups,
      preloadedVJData?.jobClickTrackingAttributes,
      url,
      viewJobModel,
      viewjobOneGraphModel,
    ],
  );

  // Get mobtk and from for logging
  const mobtk = viewJobModel?.mobtk;
  const from = fromParamUtils(url);

  // Handle screenshot sharing with job details
  useScreenshotShare(
    viewJobModel
      ? {
          shareUrl: viewJobModel.appShareUrl ?? "",
          jobTitle: viewJobModel.jobTitle ?? "",
        }
      : null,
  );

  // Create wrapped panResponder that logs first interaction
  const panHandlers = useLogFirstUserInteractionWithHandlers(
    mobtk,
    from,
    originalPanResponder.panHandlers,
  );

  // Mark as interactive when ViewJob data is loaded and ready
  useEffect(() => {
    if (viewJobModel) {
      markInteractive();
    }
  }, [viewJobModel, markInteractive]);

  //Store the latest values for unmount logging
  const latestQueryStatusRef = useRef(queryStatus);
  const latestViewJobModelRef = useRef(viewJobModel);

  // Update refs with latest values
  useEffect(() => {
    latestQueryStatusRef.current = queryStatus;
    latestViewJobModelRef.current = viewJobModel;
  }, [queryStatus, viewJobModel]);

  useEffect(() => {
    return () => {
      const renderTime = renderStarts ? Date.now() - renderStarts : -1;
      const latestViewJobModel = latestViewJobModelRef.current;
      // fire and forget
      logVJUnmount({
        queryStatus: latestQueryStatusRef.current,
        duration: renderTime,
        url: url ?? "",
        jobKey: latestViewJobModel?.jobKey,
        mobtk: latestViewJobModel?.mobtk,
      }).catch(console.error);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only runs on mount/unmount

  return (
    <ViewJobDataContext.Provider value={viewJobContextValue}>
      <OneGraphApolloClientProvider
        fetch={fetchNativeOneGraphOperation}
        defaultOptions={defaultOptions}
        platformOptions={platformOptions}
        rootTrackingKey={viewJobModel?.mobtk}
        prforceGroups={prforceGroups ?? undefined}
        errorLink={errorLink}
        restoreWindowApolloState={false}
      >
        <LoggerContext.Provider value={LOGGER}>
          <ConditionalSafeAreaWrapper withTopSafeArea={withTopSafeArea} panHandlers={panHandlers}>
            <NavigationBar
              url={url}
              showSaveJobButton={queryStatus === "success"}
              showShareButton={queryStatus === "success"}
              isLoggedIn={viewJobModel?.loggedIn ?? false}
              reload={retryRequest}
              jobTitle={viewJobModel?.jobTitle}
              renderedOn={viewJobSource}
            />
            <SafeAreaView style={styles.container} edges={["left", "bottom", "right"]}>
              <ViewJobLoadingState
                status={queryStatus}
                errorMesssage={queryState.status === "error" ? queryState.error : undefined}
                onRetry={retryRequest}
                url={url}
                wasPreloaded={preloadedVJData != null}
              >
                {viewJobModel && (
                  <AppVJLoadedContent
                    url={url}
                    reload={retryRequest}
                    navigationStartTimeMs={nativeContext?.navigationStartTimeMs ?? 0}
                    fetchStartTimeMs={fetchStartTimeMs}
                    fetchEndTimeMs={fetchEndTimeMs}
                    pageLoadSignalAlreadyEmitted={preloadedVJData?.pageLoadSignalAlreadyEmitted}
                  />
                )}
              </ViewJobLoadingState>
            </SafeAreaView>
          </ConditionalSafeAreaWrapper>
        </LoggerContext.Provider>
      </OneGraphApolloClientProvider>
    </ViewJobDataContext.Provider>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
  },
  container: {
    flex: 1,
    justifyContent: Platform.OS === "web" ? "flex-start" : "center",
  },
});
